import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Card, CardActions, CardContent, CardHeader, CircularProgress, Box, Tooltip, Chip, IconButton } from '@mui/material';
import { Edit, Delete, CardGiftcard, Add, DragIndicator } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { deleteCardDeck, fetchAllDecks, sortCardDeck, selectCardDeck } from '../../../actions/cardDeckActions';
import Swal from 'sweetalert2';
import { DndContext, DragOverlay, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

const DeckItem = ({ deck, handleDelete, dispatch, navigate, dragHandleProps }) => {
  const cardCountLabel = deck.active_card_count === 0
    ? "No cards"
    : deck.active_card_count === 1
      ? deck.active_card_count + " card"
      : deck.active_card_count + " cards";

  const cardTotalCountLabel = deck.card_count === 0
    ? "No cards"
    : deck.card_count === 1
      ? deck.card_count + " total cards"
      : deck.card_count + " total cards";

  const buildSubheader = deck => {
    let subheader = 'No Category';

    if (deck.category) {
      subheader = deck.category.category_title;
    }

    if (deck.subcategory) {
      subheader += ' / ' + deck.subcategory.subcategory_title;
    }

    return subheader;
  };

  return (
    <Card sx={{ position: 'relative' }}>
      <CardHeader
        title={deck.name}
        subheader={buildSubheader(deck)}
        action={
          <Box>
            <Tooltip title={cardTotalCountLabel} placement="bottom">
              <Chip
                size='small'
                label={cardCountLabel}
                color={'info'}
                sx={{ marginRight: 1 }}
              />
            </Tooltip>
            <Chip
              size='small'
              label={deck.is_active ? 'Active' : 'Inactive'}
              color={deck.is_active ? 'success' : 'warning'}
            />
            <IconButton {...dragHandleProps}>
              <DragIndicator />
            </IconButton>
          </Box>
        }
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {deck.description}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button size="small" color="secondary" startIcon={<CardGiftcard />} component={Link} to={`/card-decks/${deck.id}/cards`} onClick={(e) => {
          e.preventDefault();
          dispatch(selectCardDeck(deck, () => navigate(`/card-decks/${deck.id}/cards`)));
        }}>
          Cards
        </Button>
        <Button size="small" color="primary" startIcon={<Edit />} component={Link} to={`/card-decks/${deck.id}`} onClick={(e) => {
          e.preventDefault();
          dispatch(selectCardDeck(deck, () => navigate(`/card-decks/${deck.id}`)));
        }}>
          Edit
        </Button>
        <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(deck.id)}>
          Delete
        </Button>
      </CardActions>
    </Card>
  );
};

const SortableItem = ({ deck, dispatch, navigate, handleDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: deck.id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
  };

  return (
    <Box sx={{ width: '100%' }}>
      <div ref={setNodeRef} style={style}>
        <DeckItem
          deck={deck}
          handleDelete={handleDelete}
          dispatch={dispatch}
          navigate={navigate}
          dragHandleProps={{ ...attributes, ...listeners }}
        />
      </div>
    </Box>
  );
};

function CardDecksMainPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector((state) => state.card_decks.allLoading);
  const deleteLoading = useSelector(state => state.card_decks.loading);
  const decks = useSelector((state) => state.card_decks.decks);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor)
  );

  useEffect(() => {
    dispatch(fetchAllDecks());
  }, [dispatch]);

  const handleDelete = deckId => {
    Swal.fire({
      title: `Are you sure?`,
      text: 'You will not be able to recover this deck!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel'
    }).then((result) => {
      if (result.value) {
        dispatch(deleteCardDeck(deckId));
      }
    });
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = decks.findIndex(deck => deck.id === active.id);
      const newIndex = decks.findIndex(deck => deck.id === over.id);
      const updatedDecks = arrayMove(decks, oldIndex, newIndex);
      dispatch(sortCardDeck(updatedDecks));
    }

    setActiveId(null);
  };

  return (
    <MainContentContainer>
      <LoadingBackdrop open={deleteLoading} />
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Card Decks
          </Typography>
          <Button color="inherit" component={Link} to={`/card-decks/create`} startIcon={<Add />}>
            Add New Deck
          </Button>
        </Toolbar>
      </AppBar>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={decks} strategy={verticalListSortingStrategy}>
            <Box sx={{ display: 'grid', gap: 2, marginTop: 2 }}>
              {decks.map(deck => (
                <SortableItem key={deck.id} deck={deck} handleDelete={handleDelete} dispatch={dispatch} navigate={navigate} />
              ))}
            </Box>
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <Box sx={{ width: '100%' }}>
                <DeckItem
                  deck={decks.find(deck => deck.id === activeId)}
                  handleDelete={handleDelete}
                  dispatch={dispatch}
                  navigate={navigate}
                  dragHandleProps={{}} // Empty object for overlay
                />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </MainContentContainer>
  );
}

export default CardDecksMainPage;